import axios from "./request";
import { toast } from "react-toastify";
import { API_PATH } from "../utils/config";
import { SIGNUP_TIMEOUT_MS } from "./signupTimeout";

// fix-fe-5178-own-profile-fetch-still-2-3x-followup — redux's
// getUserProfileByIdAction (redux/actions/user.js) and react-query's
// useGetUserProfileById (services/queries.js) both used to call this same
// endpoint through independent code paths with independent caches, so an
// in-flight dedupe living in only one of them (the redux thunk's old
// module-level Map) never saw requests started by the other. MEASURED live
// on web-dev.iyuga.net: navigating to /gallery/:id (own profile) still fired
// 2-3x GET /api/v1/user/{id}/profile/ even after PR#5178 consolidated all 3
// redux-thunk mount sites onto getOwnProfileIfNeededAction, because
// Gallery/index.jsx's react-query `profileData` fetch (queries.js) raced the
// redux fetch independently — shouldSkipOwnProfileFetch only skips it when
// redux's fetch already fully resolved before Gallery mounts, which the
// comment in galleryLoadState.js documents as NOT reliably true. Centralizing
// the in-flight dedupe here, keyed by a normalized string id (Map keys use
// strict equality; call sites pass a mix of numeric and route-param-string
// ids for the same user), makes every caller of getUserProfileById share ONE
// coalesced request regardless of which layer (redux or react-query)
// initiated it. api.getUserProfileById.dedupe.test.js and its
// .crossLayerDedupe sibling pin this Map directly against a MOCKED ./request
// (no transport-level help available), so it stays HERE rather than being
// collapsed onto `./request`'s own transport-level coalescer below.
//
// ISP-9999 (gallery dup-API fix, request.inFlightDedupe.test.js): this Map
// used to hand every coalesced caller the EXACT SAME response object instead
// of an independent copy — one caller mutating `.data` corrupted every
// sibling (MEASURED: that test's "hands each coalesced caller an
// INDEPENDENT response" case). Clone per caller, the same `cloneResponse`
// shape `./request`'s own transport-level coalescer already uses for the
// react-query path that calls `API.get` directly and never reaches this Map
// at all — so a legacy caller here gets the same mutation-safety guarantee.
const inFlightProfileFetches = new Map();

const cloneProfileResponse = (res) => {
  if (!res || typeof res !== "object") return res;
  const data = res.data;
  const clonedData =
    data && typeof data === "object" ? { ...data } : data;
  return { ...res, data: clonedData };
};

export const getUserProfileById = (user_id) => {
  const key = String(user_id);
  let fetchPromise = inFlightProfileFetches.get(key);
  if (!fetchPromise) {
    fetchPromise = axios.get(API_PATH.GET_USER_PROFILE_BY_ID(user_id));
    inFlightProfileFetches.set(key, fetchPromise);
    fetchPromise.finally(() => inFlightProfileFetches.delete(key));
  }
  return fetchPromise.then(cloneProfileResponse);
};

export const getUserWheels = (user_id, query) => {
  return axios.get(API_PATH.GET_USER_WHEELS_BY_ID(user_id, query));
};

export const getUserWheelsV2 = (query) => {
  return axios.get(API_PATH.GET_USER_WHEELS_BY_ID_V2(query));
};

// QA-1036 — the wheel pickers (Capture "Add to existing Wheel" + Chat "Share a
// Wheel") could only ever reach the FIRST `page_size=40` page: they fetched
// getUserWheels(userId) once and searched/sorted that page client-side, so a
// user with >40 wheels (e.g. count=131) could file a capture to only ~30% of
// them and could not even search for the rest (the search box never queried the
// API). This pages through EVERY DRF page (following `next`) and returns the
// merged list, so the pickers' existing client-side search/sort span the whole
// set. `maxPages` is a safety valve: a backend that never clears `next` (or a
// runaway count) can never spin this loop forever.
//
// ISP-9999 — relying on `next` alone left the Capture "Attach to Wheels"
// picker stuck on "Loading your wheels…" indefinitely for gordon-21
// (count=149): a `next` field that never clears on some pages made this loop
// keep firing sequential requests all the way to `maxPages` (200) before the
// query ever resolved, even though every wheel was already in hand after the
// 4th page. We ALSO stop as soon as we've collected `count` wheels — the
// number the very first response already told us — so a flaky `next` can no
// longer force ~200 wasted round-trips before the picker can render data it
// already has.
//
// relost-freeze-reverify-capture-attach-picker-perf-24s — #4711 (above) fixed
// the *infinite*-spin case but never touched the underlying fetch shape: this
// was still a strictly sequential `await`-per-page loop, so a heavily
// populated account (e.g. count=800, 20 pages at page_size=40) still took one
// full page's round-trip time PER PAGE — ~24s wall time on staging's real
// per-page query cost (500ms-1.5s * 20 pages), even though nothing about page
// 2..N depends on page 1's result. Once page 1's response reveals `count`, the
// exact number of remaining pages is already known (page_size is fixed at 40
// by GET_USER_WHEELS_BY_ID), so fetch them all CONCURRENTLY via `Promise.all`
// instead of one round-trip at a time. Only fall back to the old next-following
// sequential loop (still capped by maxPages) when `count` is missing/unusable
// (e.g. a bare-array/non-paginated response) — that fallback is exercised by
// backends that don't report a total at all, not by a well-formed `count`.
//
// `extraQuery` is opt-in and additive to the `&page=` param below — e.g. chat's
// Share-a-Wheel picker passes `&include_collaborations=true` so the list also
// includes wheels the user collaborates on (not just owns). Every other caller
// (Capture's AttachWheelModal) omits it and gets byte-identical behavior.
const USER_WHEELS_PAGE_SIZE = 40; // must match GET_USER_WHEELS_BY_ID's page_size=40

const extractPageResults = (d) =>
  Array.isArray(d) ? d : Array.isArray(d?.results) ? d.results : d?.data ?? [];

export const getAllUserWheels = async (
  user_id,
  { maxPages = 200, extraQuery = "" } = {}
) => {
  const first = await getUserWheels(user_id, `&page=1${extraQuery}`);
  const firstData = first?.data ?? {};
  let results = extractPageResults(firstData);
  results = Array.isArray(results) ? results : [];
  const count = typeof firstData.count === "number" ? firstData.count : 0;

  const countUsable = typeof firstData.count === "number" && firstData.count >= 0;

  if (!firstData || !firstData.next) {
    // Single page (or a non-paginated bare-array response) — nothing more to
    // fetch, unchanged from the old behaviour.
    return { data: { results, count } };
  }

  if (countUsable && results.length < count) {
    // We already know exactly how many pages remain (page_size is fixed) —
    // fetch them all at once instead of following `next` one at a time.
    const totalPages = Math.min(
      Math.ceil(count / USER_WHEELS_PAGE_SIZE),
      maxPages
    );
    const remainingPages = [];
    for (let page = 2; page <= totalPages; page += 1) remainingPages.push(page);

    if (remainingPages.length > 0) {
      const pageResponses = await Promise.all(
        remainingPages.map((page) => getUserWheels(user_id, `&page=${page}${extraQuery}`))
      );
      for (const res of pageResponses) {
        const pageResults = extractPageResults(res?.data ?? {});
        results = results.concat(Array.isArray(pageResults) ? pageResults : []);
      }
    }
    return { data: { results, count } };
  }

  // Fallback: `count` is missing/unusable — follow `next` sequentially, still
  // capped by maxPages so a `next` that never clears can't spin forever.
  for (let page = 2; page <= maxPages; page += 1) {
    const res = await getUserWheels(user_id, `&page=${page}${extraQuery}`);
    const d = res?.data ?? {};
    const pageResults = extractPageResults(d);
    results = results.concat(Array.isArray(pageResults) ? pageResults : []);
    if (typeof d.count === "number" && results.length >= d.count) break;
    if (!d || !d.next) break;
  }
  return { data: { results, count } };
};

export const fetchWheels = async ({ pageParam = 1 }) => {
  const response = await axios.get("/api/v2/wheel/", {
    params: {
      page: pageParam, // Current page to fetch
      page_size: 20, // Number of results per page
      user_id: 8,
    },
  });
  return response.data;
};

export const getBgWallpapers = () => {
  return axios.get(API_PATH.GET_BG_WALLPAPERS);
};

export const getPrimaryWheel = (userId) => {
  return axios.get(API_PATH.GET_PRIMARY_WHEEL);
};

export const createUserWheel = (data) => {
  return axios.post(API_PATH.CREATE_WHEEL, data);
};

export const getAiWheel = (temp_id) => {
  return axios.get(API_PATH.GET_LATEST_AI_WHEEL(temp_id));
};

export const generateAiWheel = (data) => {
  return axios.post(API_PATH.GENERATE_AI_WHEEL, data);
};

// AI Wheel Builder staged funnel (D14). Both endpoints 404 unless the server
// flag AI_FUNNEL_ENABLED is on; callers must gate the UI on REACT_APP_AI_FUNNEL.
export const aiFunnelScrapeWebsite = (url) => {
  return axios.post(API_PATH.AI_FUNNEL_SCRAPE, { url });
};

// Y7: DEAD until the Segments & Cost review stage (deck 02c/02f) is built — no
// caller invokes this yet. Keep it wired for that stage; do not treat its
// presence as evidence the cost-estimate UI exists.
export const aiFunnelEstimateCost = (data) => {
  return axios.post(API_PATH.AI_FUNNEL_ESTIMATE, data);
};

// AI Wheel Lab (gordon-0923-P1, DEV-ONLY staff page, src/pages/AiWheelLab).
// Body {prompt, segment_count} — instant, no model call server-side.
export const aiWheelLabEstimate = (data) => {
  return axios.post(API_PATH.AI_WHEEL_LAB_ESTIMATE, data);
};

// Body {prompt, segment_count, documents} — fans out to every server-configured
// model in parallel (see Backend PR #3402, yuga_wheel/services/ai_wheel_lab.py).
export const aiWheelLabGenerate = (data) => {
  return axios.post(API_PATH.AI_WHEEL_LAB_GENERATE, data);
};

// AI Wheel Copilot (D13): advice + proposed edits for the open Wheel.
// Server-authoritative: tier gate + AI Wheel Token billing + op whitelist all
// happen on the backend; this just carries {wheel_state, ask}.
export const askWheelCopilot = (data) => {
  return axios.post(API_PATH.WHEEL_COPILOT, data);
};

export const getMyCustomColors = () => {
  return axios.get(API_PATH.GET_MY_CUSTOM_COLORS());
};

export const addMyCustomColor = (data) => {
  return axios.post(API_PATH.ADD_MY_CUSTOM_COLORS(), data);
};

export const editMyCustomColor = (id, data) => {
  return axios.patch(API_PATH.EDIT_MY_CUSTOM_COLORS(id), data);
};

export const deleteMyCustomColor = (id) => {
  return axios.delete(API_PATH.EDIT_MY_CUSTOM_COLORS(id));
};
export const duplicateWheel = (data) => {
  return axios.post(API_PATH.CLONE_WHEEL(data.WheelID), data.data);
};
export const search = (text) => {
  return axios.get(API_PATH.GENERAL_SEARCH(text));
};

export const searchUsers = (search, page = 1, page_size = 10) => {
  return axios.get(API_PATH.SEARCH_USERS(search, page, page_size));
};

export const searchWheels = (search, page = 1, page_size = 10) => {
  return axios.get(API_PATH.SEARCH_WHEELS(search, page, page_size));
};

// #920 §5 — collections (renamed folders) for the global search's Collections
// tab. Returns the signed-in user's collections; SearchBar filters them by name
// through FoldersV2/collectionSearch.js. See API_PATH.SEARCH_COLLECTIONS.
export const searchUserCollections = (search, userId, page = 1, page_size = 50) => {
  return axios.get(
    API_PATH.SEARCH_COLLECTIONS({ user: userId, search, page, page_size })
  );
};

export const cloneWheel = (id, data) => {
  return axios.post(API_PATH.CLONE_WHEEL(id), data);
};

export const cloneSegment = (id) => {
  return axios.post(API_PATH.CLONE_SEGMENT(id));
};

// SEGMENT COLLECT §1 — the current user's collected segments (each rendered as a
// hollow-except-the-collected-segment Wheel in the Segment Library "Segment
// Sets" tab — renamed from "Collections" by #920a; the ROUTE is unchanged).
export const getSegmentCollections = (page = 1, page_size = 12) => {
  return axios.get(API_PATH.SEGMENT_COLLECTIONS(page, page_size));
};

export const likeUnlikeWheel = (id) => {
  return axios.post(API_PATH.LIKE_UNLIKE_WHEEL(id));
};

export const getAllTransactions = (
  startDate,
  endDate,
  limit = 10,
  offset = 0
) => {
  return axios.get(
    API_PATH.GET_ALL_TRANSACTIONS(startDate, endDate, limit, offset)
  );
};

export const getAllCards = () => {
  return axios.get(API_PATH.LIST_ALL_CARDS);
};

export const removeCard = (id) => {
  return axios.post(API_PATH.DELETE_CARD(id));
};

export const getCurrentPlan = () => {
  return axios.get(API_PATH.GET_ACTIVE_SUBSCRIPTION);
};

// T22.242: itemized next bill (plan + extra team seats + add-ons, date, total).
export const getNextBill = () => {
  return axios.get("/payments/next-bill/");
};

export const canCreateWheel = () => {
  return axios.get(API_PATH.CAN_CREATE_WHEEL);
};

export const canCloneWheel = () => {
  return axios.get(API_PATH.CAN_CLONE_WHEEL);
};

export const getSubscriptionComparison = (data) => {
  return axios.get(API_PATH.COMPARE_SUBSCRIPTION, { params: data });
};

export const downloadInvoice = (data) => {
  return axios.post(API_PATH.DOWNLOAD_INVOICE, data);
};

// Referrals

export const getAmbassadorReferral = (queryParams) => {
  const { sub_filter, ...otherParams } = queryParams;

  // Build query string manually for sub_filter to avoid array notation
  let queryString = "";
  const searchParams = new URLSearchParams();

  // Add other parameters
  Object.entries(otherParams).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      searchParams.append(key, value);
    }
  });

  // Add sub_filter parameters individually
  if (sub_filter && Array.isArray(sub_filter)) {
    sub_filter.forEach((filter) => {
      searchParams.append("sub_filter", filter);
    });
  }

  queryString = searchParams.toString();
  const url = queryString
    ? `${API_PATH.REFERRAL_AMBASSADOR}?${queryString}`
    : API_PATH.REFERRAL_AMBASSADOR;

  return axios.get(url);
};

export const getBtReferral = (queryParams) => {
  const { sub_filter, ...otherParams } = queryParams;

  // Build query string manually for sub_filter to avoid array notation
  let queryString = "";
  const searchParams = new URLSearchParams();

  // Add other parameters
  Object.entries(otherParams).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      searchParams.append(key, value);
    }
  });

  // Add sub_filter parameters individually
  if (sub_filter && Array.isArray(sub_filter)) {
    sub_filter.forEach((filter) => {
      searchParams.append("sub_filter", filter);
    });
  }

  queryString = searchParams.toString();
  const url = queryString
    ? `${API_PATH.REFERRAL_BT}?${queryString}`
    : API_PATH.REFERRAL_BT;

  return axios.get(url);
};

export const getDirectReferral = (queryParams) => {
  const { sub_filter, ...otherParams } = queryParams;

  // Build query string manually for sub_filter to avoid array notation
  let queryString = "";
  const searchParams = new URLSearchParams();

  // Add other parameters
  Object.entries(otherParams).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      searchParams.append(key, value);
    }
  });

  // Add sub_filter parameters individually
  if (sub_filter && Array.isArray(sub_filter)) {
    sub_filter.forEach((filter) => {
      searchParams.append("sub_filter", filter);
    });
  }

  queryString = searchParams.toString();
  const url = queryString
    ? `${API_PATH.REFERRAL_DIRECT}?${queryString}`
    : API_PATH.REFERRAL_DIRECT;

  return axios.get(url);
};

export const getAnalytics = (queryParams) => {
  const params = {
    ...queryParams,
    limit: 10,
  };
  return axios.get(API_PATH.ANALYTICS, { params });
};

export const sendReferralInvites = (data) => {
  return axios.post(API_PATH.REFERRAL_INVITES_API(), data);
};

export const getBalance = () => {
  return axios.get(API_PATH.GET_BALANCE);
};

export const getTokenBundles = () => {
  return axios.get(API_PATH.GET_TOKEN_BUNDLES);
};

export const purchaseTokenBundle = (data) => {
  return axios.post(API_PATH.PURCHASE_TOKEN_BUNDLE, data);
};

export const connectStripeAccount = (data) => {
  return axios.post(API_PATH.CONNECT_STRIPE_API(), data);
};

// Owner-only, idempotent backstop that (re)syncs the caller's own current Stripe
// subscription. Called after the subscription-success redirect so activation
// never depends solely on Stripe webhook delivery.
export const syncSubscription = () => {
  return axios.post(API_PATH.SYNC_SUBSCRIPTION, {});
};

export const getStripeAccountStatus = () => {
  return axios.get(API_PATH.GET_STRIPE_ACCOUNT_STATUS);
};

export const getReferralsChart = (category, time) => {
  return axios.get(API_PATH.REFERRAL_CHART(category, time));
};

export const downloadReferralsReport = (query) => {
  return axios.get(API_PATH.DOWNLOAD_REFERRALS_REPORT(query));
};

export const getHallOfFame = (query, startDate, endDate) => {
  return axios.get(API_PATH.GET_HALL_OF_FAME(query, startDate, endDate));
};

export const getFollowersGained = (query, startDate, endDate) => {
  return axios.get(API_PATH.GET_FOLLOWERS_GAINED(query, startDate, endDate));
};

export const getAnalyticsDetail = (queryParams) => {
  const params = {
    ...queryParams,
    limit: 10,
  };
  return axios.get(API_PATH.GET_ANALYTICS_DETAIL, { params });
};

// gordon-0923-P1-analytics-charts-over-time (ADDENDUM 2): day-by-day
// account/per-wheel series. `queryParams` is the already-shaped
// { start_date, end_date, wheel_id?, compare? } object -- see
// services/queries.js's useAnalyticsTimeseries for the Date -> string
// normalization (mirrors getAnalytics's thin params-passthrough style, not
// GET_HALL_OF_FAME's own hand-built querystring).
export const getAnalyticsTimeseries = (queryParams) => {
  return axios.get(API_PATH.ANALYTICS_TIMESERIES, { params: queryParams });
};

export const getWheelEmailPermission = (id) => {
  return axios.get(API_PATH.GET_WHEEL_EMAIL_PERMISSION(id));
};

export const deleteWheelEmailPermission = (id, data) => {
  return axios.post(API_PATH.DELETE_WHEEL_EMAIL_PERMISSION(id), data);
};

export const uploadImage = (data) => {
  return axios.put(API_PATH.UPLOAD_IMAGE, data, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

export const getPrimaryWheelV2 = (id) => {
  return axios.get(API_PATH.GET_PRIMARY_WHEEL_V2(id));
};

export const getPrimaryOrTopWheelV2 = (id) => {
  return axios.get(API_PATH.GET_PRIMARY_OR_TOP_WHEEL_V2(id));
};

export const getQueriedUserWheels = (user_id, query) => {
  return axios.get(API_PATH.GET_USER_WHEELS(user_id, query));
};

export const signupBeta = (data) => {
  return axios.post(API_PATH.SIGNUP_BETA_V2(), data);
};

export const avifToPng = (file) => {
  const formData = new FormData();
  formData.append("avif_file", file);

  return axios.post(API_PATH.AVIF_T0_PNG, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

// Fetch Palette Library
export const getPaletteLibrary = (wheelId, type, search, usageType) => {
  return axios.get(
    API_PATH.GET_PALETTE_LIBRARY(wheelId, type, search, usageType)
  );
};

// Delete a Palette
export const deletePalette = (paletteId) => {
  return axios.delete(API_PATH.DELETE_PALETTE(), {
    data: { id: paletteId }, // Send ID in the request body
  });
};

// Generate a Scheme
// review-0921-fe-5955-followups NB-1: bounded so a hung request can't leak a
// paletteInFlight begin() forever (see hooks/paletteInFlight.js's watchdog,
// the second line of defense for whatever a timeout doesn't catch).
export const generateScheme = (data) => {
  return axios.post(API_PATH.GENERATE_SCHEME(), data, { timeout: 20000 });
};

// Save a Palette
export const savePalette = (paletteData) => {
  return axios.post(API_PATH.SAVE_PALETTE(), paletteData, { timeout: 20000 });
};

// ── Theme library (SavedTheme) ─────────────────────────────────────────────
export const getThemeLibrary = (type, search) => {
  return axios.get(API_PATH.GET_THEME_LIBRARY(type, search));
};

export const saveTheme = (themeData) => {
  return axios.post(API_PATH.SAVE_THEME(), themeData);
};

export const renameTheme = (data) => {
  return axios.post(API_PATH.RENAME_THEME, data);
};

export const deleteTheme = (themeId) => {
  return axios.delete(API_PATH.DELETE_THEME(), { data: { id: themeId } });
};

// Browse ALL saved color palettes across every Wheel (no wheel scope). Reuses
// the existing palette-library endpoint: passing an empty wheel_id makes the
// backend skip its "if ... and wheel_id" filter, so no backend change needed.
export const getAllColorPalettes = (type, search, usageType = 2) => {
  return axios.get(API_PATH.GET_PALETTE_LIBRARY("", type, search, usageType));
};

// Generate Colors from Image
export const generateColorsFromImage = (imageUrl, count = 5) => {
  return axios.post(API_PATH.GENERATE_COLORS_FROM_IMAGE(), {
    image_url: imageUrl,
    count,
  });
};

export const generateColorsFromPrompt = (data) => {
  return axios.post(API_PATH.GENERATE_COLORS_FROM_PROMPT(), data);
};

export const saveRecentMedia = async (wheelId, mediaData) => {
  const response = await axios.post(API_PATH.SAVE_RECENT_MEDIA(wheelId), {
    wheel: wheelId,
    ...mediaData,
  });
  // The backend dedupes recent media by (user, wheel, url). When this media is
  // already saved to the wheel it returns already_exists=true instead of
  // creating a duplicate row. Let the user know instead of silently proceeding.
  // Only relevant when adding to an actual wheel (wheelId present).
  if (wheelId && response?.data?.data?.already_exists) {
    toast.warn("It's already been added to that wheel.");
  }
  return response;
};

export const saveExternalImage = (data) => {
  return axios.post(API_PATH.SAVE_EXTERNAL_IMAGE(), data);
};

// EMBED_URL_REHOST: fetch + moderate + re-host a pasted external image URL
// server-side; returns { url } (the S3 url) on pass, or a 400 with
// { data: { flagged: true }, message } when moderation rejects the image.
export const rehostEmbedImage = (data) => {
  return axios.post(API_PATH.EMBED_URL_REHOST(), data);
};

// AUDIO_LINK_EMBED: resolve a pasted audio link (Spotify/Apple Music/YouTube
// Music/SoundCloud/Bandcamp/Mixcloud/Deezer/Tidal/podcast RSS/…) to safe
// display metadata + an embed URL or on-brand audioSrc. Never send/receive
// raw HTML. Returns { provider, kind, title, author, thumbnail, embedUrl,
// audioSrc, theme } on success, or a 400 with a plain generic message.
export const resolveAudioLink = (data) => {
  return axios.post(API_PATH.RESOLVE_AUDIO_LINK(), data);
};

// stgfix-1007-P1-iphone-video-upload-rejected: called right after a Segment
// Video block upload completes. An HEVC-encoded upload (iPhone's default
// camera codec) uploads fine but can't be decoded by any Chromium/Firefox
// browser; the backend swaps it for a freshly-transcoded H.264 copy when
// needed (SEGMENT_VIDEO_WEB_TRANSCODE_ENABLED). Returns
// { url, transcoded } -- a non-HEVC upload's own url comes back unchanged.
// T22.354: `async: true` -> the backend answers at once and converts on a
// worker; `pending: true` means call again (UploadMedia polls). A backend
// without the async path ignores the flag and answers the old way.
export const checkSegmentVideoWeb = (videoUrl) => {
  return axios.post(API_PATH.SEGMENT_VIDEO_WEB(), {
    video_url: videoUrl,
    async: true,
  });
};

// Get Recent Media
export const getRecentMedia = (wheelId, type, page) => {
  return axios.get(API_PATH.GET_RECENT_MEDIA(wheelId, type, page));
};

// Get total storage
export const getTotalStorage = () => {
  return axios.get(API_PATH.GET_TOTAL_STORAGE());
};

// Get v2 storage usage (backend #1121). May serve zeros until counting is on.
export const getStorageUsage = () => {
  return axios.get(API_PATH.GET_STORAGE_USAGE());
};

// Get all storage
export const getTotalMedia = () => {
  return axios.get(API_PATH.GET_TOTAL_MEDIA());
};

// gordon-1005-P1-media-library-folders-build: file a Media Library item
// (addressed by its S3 `url`, since standalone-page items have no DB row)
// into an existing folder (`folderId`) or a brand-new one (`folderName`,
// create-if-needed server-side).
export const moveMediaToFolder = ({ url, mediaType = 0, folderId, folderName }) => {
  return axios.put(API_PATH.MOVE_MEDIA_TO_FOLDER(), {
    url,
    media_type: mediaType,
    ...(folderId != null ? { folder_id: folderId } : { folder_name: folderName }),
  });
};

// Create a new Media Library folder (same /api/v2/folders/ Collections uses).
export const createMediaFolder = (name) => {
  return axios.post(API_PATH.CREATE_FOLDER, { name });
};

// Delete a Media Library folder. BE SET_NULLs every WheelRecentMedia row
// filed into it (migration 0226) -- the folder's media is never deleted.
export const deleteMediaFolder = (id) => {
  return axios.delete(API_PATH.DELETE_FOLDER(id));
};

// Delete selected media FILES by their S3 storage paths.
// The `api/media/delete/` endpoint has two branches keyed off the request body:
//   { paths: [<s3 key strings>] }   -> ownership-checked (has_delete_permission) file delete
//   { wheel_ids: [<int>] }          -> whole-wheel delete (see deleteWheelsFromStorage)
// This function is the FILE branch, so it must send `paths` (strings). It was
// previously sending `{ wheel_ids: paths }`, which the BE serializer rejected
// (wheel_ids is an IntegerField) with 400 "A valid integer is required" — the
// delete silently failed. (ISP media-library delete fix.)
export const deleteMediaFiles = (paths) => {
  return axios.post(API_PATH.DELETE_MEDIA(), {
    paths,
  });
};

// Rename one Media Library file. `path` is its existing S3 storage path (the
// same identifier deleteMediaFiles above uses) and `name` is the new display
// name — the S3 key/URL is never touched (gordon-0831-media-library-rename).
export const renameMedia = (path, name) => {
  return axios.post(API_PATH.RENAME_MEDIA(), {
    path,
    name,
  });
};

// WL-072: which of the caller's own Wheels (id, name, thumbnail) reference
// each of the given storage paths. Powers the delete modal's Wheel checklist.
// Inert (`{tracked:false, wheels:{}}`) while the BE's MEDIA_LIFECYCLE_SAFE_DELETE
// flag is off — callers must treat `tracked:false` as "unknown", not "unused".
export const getMediaWheels = (paths) => {
  return axios.post(API_PATH.GET_MEDIA_WHEELS(), {
    paths,
  });
};

// WL-072: detach one media file from a caller-selected SET of Wheels — the
// "remove from selected Wheels" alternative to a permanent Library delete.
// Does NOT delete the file itself (unlike deleteMediaFiles); the S3 object and
// the Library entry are untouched, only those Wheels' references are removed.
export const removeMediaFromWheels = (path, wheelIds) => {
  return axios.post(API_PATH.REMOVE_MEDIA_FROM_WHEELS(), {
    path,
    wheel_ids: wheelIds,
  });
};

// Delete whole WHEELS (and their associated media) from Storage by wheel id.
// Hits the same endpoint's wheel branch: the BE auto-sets delete_wheels=True
// whenever `wheel_ids` is present. Kept separate from deleteMediaFiles so the
// two request shapes (int ids vs s3 path strings) never get conflated — routing
// wheel ids through the `paths` branch would 404 (s3 head_object on an int key).
export const deleteWheelsFromStorage = (wheelIds) => {
  return axios.post(API_PATH.DELETE_MEDIA(), {
    wheel_ids: wheelIds,
  });
};

//delete all media
export const deleteAllMedia = () => {
  return axios.post(API_PATH.DELETE_ALL_MEDIA());
};

//get media by type
export const getMediaByCategory = (type, pageToken) => {
  return axios.get(API_PATH.GET_MEDIA_BY_CATEGORY(type, pageToken));
};
export const getContentModerationListing = (status, search, pageToken) => {
  return axios.get(
    API_PATH.GET_CONTENT_MODERATION_LISTING(status, search, pageToken)
  );
};

// ── MOD-1 staff Moderation Console (flag-gated; BE staff-only + 404 while OFF) ──
export const getModerationQueue = (params) =>
  axios.get(API_PATH.MODERATION_QUEUE(params));
export const getModerationStats = () => axios.get(API_PATH.MODERATION_STATS());
export const getModerationFlag = (id) =>
  axios.get(API_PATH.MODERATION_DETAIL(id));
export const approveModerationFlag = (id, note) =>
  axios.post(API_PATH.MODERATION_APPROVE(id), { note });
export const rejectModerationFlag = (id, note) =>
  axios.post(API_PATH.MODERATION_REJECT(id), { note });
export const reinstateModerationFlag = (id, note) =>
  axios.post(API_PATH.MODERATION_REINSTATE(id), { note });
export const resolveModerationAppeal = (id, decision, note) =>
  axios.post(API_PATH.MODERATION_APPEAL_RESOLVE(id), { decision, note });

export const getWheelMedia = (id) => {
  return axios.get(API_PATH.GET_WHEEL_MEDIA(id));
};

export const getWheelDetails = async (wheelId, queryParams = "") => {
  try {
    return await axios.get(
      `${API_PATH.GET_WHEEL_DETIALS(wheelId)}${queryParams}`
    );
  } catch (err) {
    throw err;
  }
};
// feat-0923-broken-media-alerts (SPEC.md contract): owner-only list of a
// wheel's currently-broken assigned media, powering the Edit Wheel header
// indicator + list panel.
export const getWheelBrokenMedia = (wheelId) => {
  return axios.get(API_PATH.GET_WHEEL_BROKEN_MEDIA(wheelId));
};

// feat-0923-broken-media-alerts: per-wheel broken-media counts for the
// signed-in owner (+ an avatar flag), used anywhere a lightweight summary is
// enough (e.g. a future gallery-wide indicator).
export const getBrokenMediaSummary = () => {
  return axios.get(API_PATH.GET_BROKEN_MEDIA_SUMMARY());
};

// feat-0923-broken-media-alerts client reporter: fire-and-forget — any authed
// or anonymous viewer's browser reports a load failure on ASSIGNED media (a
// Segment Fill / Image or media Block / Wallpaper / Wheel Profile / Avatar
// image that failed to load). The server re-verifies the url before recording
// anything, so a stale/transient client-side failure can't poison the record;
// this call must never throw into or block the caller's render path.
export const reportBrokenMedia = ({ wheelId, kind, segmentId, blockId, url } = {}) => {
  try {
    return axios
      .post(API_PATH.POST_BROKEN_MEDIA_REPORT(), {
        wheel_id: wheelId ?? undefined,
        kind,
        segment_id: segmentId ?? undefined,
        block_id: blockId ?? undefined,
        url,
      })
      .catch(() => {});
  } catch {
    return Promise.resolve();
  }
};

//get notifications
export const getNotifications = (page) => {
  return axios.get(API_PATH.GET_NOTIFICATIONS(page));
};

//mark as reak
export const markAllRead = () => {
  return axios.post(API_PATH.MARK_ALL_READ());
};

// Mark a SINGLE notification read (P5S1-O12: clicking a row now persists its
// read state instead of leaving it forever unread).
export const markNotificationRead = (id) => {
  return axios.post(API_PATH.MARK_NOTIFICATION_READ(id));
};

// Server-authoritative unread notification total (not page-limited).
// Response: { data: { unread_count: <int> } }
export const getUnreadNotificationCount = () => {
  return axios.get(API_PATH.NOTIFICATION_UNREAD_COUNT());
};

// QA-1014: delete a single notification (FB-style kebab Delete). Hits the DRF
// ModelViewSet's default destroy route (DELETE /api/v1/notification/{id}/) which
// already exists — no new BE endpoint. Returns 204 on success.
export const deleteNotification = (id) => {
  return axios.delete(API_PATH.DELETE_NOTIFICATION(id));
};

// T22.120 (D20): `wheelId` is the open wheel; on a team wheel the BE answers
// with the team leader's plan when it is higher than the user's own.
export const checkIfUserEligible = (feature, wheelId) => {
  return axios.post(
    API_PATH.CHECK_PERMISSIONS,
    wheelId ? { feature, wheel_id: wheelId } : { feature }
  );
};

export const updateWheelPreview = async (wheelId) => {
  const response = await axios.put(
    API_PATH.TAKE_SCREENSHOT_OF_WHEEL(wheelId),
    {}
  );
  return response.data;
};

export const getCommissionStructure = () => {
  return axios.get(API_PATH.GET_COMMISSION_STRUCTURE);
};

export const getCommissionEvaluation = () => {
  return axios.get(API_PATH.GET_COMMISSION_EVALUATION);
};

export const sendWheelMedia = (wheelId, mediaData) => {
  return axios.post(API_PATH.SEND_WHEEL_MEDIA(wheelId), {
    wheel_id: wheelId,
    urls: mediaData,
  });
};

export const deleteWheelMedia = (data) => {
  return axios.post(API_PATH.DELETE_WHEEL_MEDIA(), data);
};

export const postMediaModeration = (data) => {
  return axios.post(API_PATH.POST_MEDIA_MODERATION(), data);
};

export const getProfileByReferCode = (code) => {
  return axios.get(API_PATH.GET_PROFILE_BY_REFER_CODE(code));
};

export const getLatestWheelToShowInReferModal = (id) => {
  return axios.get(API_PATH.GET_LATEST_WHEEL_TO_SHOW_IN_REFER_MODAL(id));
};

export const postWheelScreenshot = (wheelId, data) => {
  return axios.post(API_PATH.POST_SCREENSHOT(wheelId), data);
};

export const takeWheelScreenshot = (wheelId) => {
  return axios.post(API_PATH.TAKE_WHEEL_SCREENSHOT(wheelId), {});
};

export const getSegmentDetails = (segmentId) => {
  return axios.get(API_PATH.GET_SEGMENT_DETAILS(segmentId));
};

export const checkReferral = (data) => {
  // Bounded wait: see ./signupTimeout.js -- this preamble call runs BEFORE the
  // signup POST on every submit, so an unbounded hang here blocked the whole
  // Sign Up flow just as badly as an unbounded signup POST would.
  return axios.post(API_PATH.CHECK_REFERRAL(), data, { timeout: SIGNUP_TIMEOUT_MS });
};

export const renameColorPalette = (data) => {
  return axios.post(API_PATH.RENAME_COLOR_PALETTE, data);
};

export const getWheelCount = () => {
  return axios.get(API_PATH.GET_WHEEL_COUNT);
};

export const generateAICompletion = (data) => {
  return axios.post(API_PATH.GENERATE_AI_COMPLETION, data);
};

export const generateAISuggestions = (data) => {
  return axios.post(API_PATH.GENERATE_AI_SUGGESTIONS, data);
};

export const generateAITitles = (prompt, count = 1) => {
  const params = new URLSearchParams({
    prompt: prompt,
    count: count.toString(),
  });
  return axios.get(`${API_PATH.GENERATE_AI_TITLES}?${params}`);
};
